import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, catchError, finalize, shareReplay, throwError } from 'rxjs';
import { Fountain } from '../models/Fountain';
import { environment } from '../../environments/environment';

export interface SourceNumberResponse {
  number: number;
}

export interface SourceReportResponse {
  columns: string[];
  payload: Record<string, any>[];
  total: number;
  message?: string;
}

export interface GenerateNewSourceBoletaRequest {
  pkFuente: number;
  anio: number;
  mes: number;
  segFuncion: string;
  segUsuario: number;
  segTransaccion: number;
  segInfoAdicional: string | null;
}

export interface GenerateNewSourceBoletaResponse {
  request: GenerateNewSourceBoletaRequest;
  payload: Record<string, any>[];
  total: number;
}

export interface OpenPublicationMonth {
  anio: number;
  mes: number;
}

export interface OpenPublicationMonthsResponse {
  payload: OpenPublicationMonth[];
  total: number;
}

@Injectable({
  providedIn: 'root'
})
export class SourceService {
 private baseUrl = `${environment.apiUrl}/api/source`;
 private generateNumberInFlight$: Observable<SourceNumberResponse> | null = null;

  constructor(private http: HttpClient) {}

  // GET: todos los registros
  getAll(): Observable<Fountain[]> {
    return this.http.get<Fountain[]>(this.baseUrl);
  }

  getById(id: number): Observable<Fountain> {
    return this.http.get<Fountain>(`${this.baseUrl}/${id}`);
  }

  getSourceArticleCharacteristicsReport(pkFuente?: number): Observable<SourceReportResponse> {
    let params = new HttpParams();

    if (pkFuente != null) {
      params = params.set('pkFuente', pkFuente);
    }

    return this.http.get<SourceReportResponse>(
      `${this.baseUrl}/report/source-article-characteristics`,
      { params }
    );
  }

  generateNumber(): Observable<SourceNumberResponse> {
    if (this.generateNumberInFlight$) {
      return this.generateNumberInFlight$;
    }

    const request$ = this.http.get<SourceNumberResponse>(`${this.baseUrl}/generate-number`).pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
      finalize(() => {
        this.generateNumberInFlight$ = null;
      })
    );

    this.generateNumberInFlight$ = request$;
    return request$;
  }

  add(fountain: Fountain): Observable<any> {
    return this.http.post(`${this.baseUrl}/add`, fountain);
  }

  edit(fountain: Fountain): Observable<any> {
    return this.http.put(`${this.baseUrl}/edit`, fountain);
  }

  generateNewSourceBoleta(request: GenerateNewSourceBoletaRequest): Observable<GenerateNewSourceBoletaResponse> {
    const fallbackUrl = `${environment.apiUrl}/api/sources/generate-new-source-boleta`;

    return this.http
      .post<GenerateNewSourceBoletaResponse>(`${this.baseUrl}/generate-new-source-boleta`, request)
      .pipe(
        catchError((error: unknown) => {
          if (!(error instanceof HttpErrorResponse) || error.status !== 404) {
            return throwError(() => error);
          }

          return this.http.post<GenerateNewSourceBoletaResponse>(fallbackUrl, request);
        })
      );
  }

  getOpenPublicationMonths(segUsuario: number): Observable<OpenPublicationMonthsResponse> {
    const fallbackUrl = `${environment.apiUrl}/api/sources/generate-new-source-boleta/open-months`;
    const params = new HttpParams().set('segUsuario', segUsuario);

    return this.http
      .get<OpenPublicationMonthsResponse>(`${this.baseUrl}/generate-new-source-boleta/open-months`, { params })
      .pipe(
        catchError((error: unknown) => {
          if (!(error instanceof HttpErrorResponse) || error.status !== 404) {
            return throwError(() => error);
          }

          return this.http.get<OpenPublicationMonthsResponse>(fallbackUrl, { params });
        })
      );
  }

  delete(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/${id}`);
  }
  
  getPending(): Observable<Fountain[]> {
    return this.http.get<Fountain[]>(`${this.baseUrl}/pending`);
  }
}
